import React, { useState, useEffect } from 'react';
import supabase from './supabase';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import BlanksOrders from './components/BlanksOrders';
import Disputes from './components/Disputes';
import Broadcast from './components/Broadcast';
import { fetchManuLeads } from './api';
import './App.css';

const NAV_TABS = [
  { value: 'payouts', label: 'Payouts' },
  { value: 'blanks', label: 'Blanks Orders' },
  { value: 'disputes', label: 'Order Disputes' },
  { value: 'leads', label: 'Manufacturer Applications' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'broadcast', label: 'Broadcast' }
];

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('payouts');
  const [pendingLeadsCount, setPendingLeadsCount] = useState(0);

  useEffect(() => {
    // Only restore session if already verified (was an admin last time)
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        // Re-verify admin access on reload
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
        try {
          const res = await fetch(`${API_URL}/payouts/admin/pending`, {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          });
          if (res.status === 403) {
            await supabase.auth.signOut();
            setSession(null);
          } else {
            setSession(session);
          }
        } catch {
          setSession(session);
        }
      }
      setLoading(false);
    });

    // Only listen for sign-out events, not sign-in
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_OUT') {
          setSession(null);
        }
        // Don't auto-set session on SIGNED_IN — Login handles that via onLogin
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = (session) => {
    setSession(session);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
  };

  useEffect(() => {
    if (!session?.access_token) {
      setPendingLeadsCount(0);
      return;
    }

    fetchManuLeads(session.access_token, 'pending')
      .then(data => setPendingLeadsCount(data.length))
      .catch(() => setPendingLeadsCount(0));
  }, [session]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#888' }}>
        Loading...
      </div>
    );
  }

  if (!session) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="admin-layout">
      <header className="admin-header">
        <div className="admin-header-left">
          <img src="/darkTlogo.png" alt="Tchpack" className="admin-logo-img" />
          <h1>Tchpack Admin</h1>
          <nav className="admin-nav">
            {NAV_TABS.map(tab => (
              <button
                key={tab.value}
                className={`admin-nav-btn ${view === tab.value ? 'admin-nav-btn-active' : ''}`}
                onClick={() => setView(tab.value)}
              >
                {tab.label}
                {tab.value === 'leads' && pendingLeadsCount > 0 && (
                  <span className="admin-nav-badge">{pendingLeadsCount}</span>
                )}
              </button>
            ))}
          </nav>
        </div>
        <div className="admin-header-right">
          <button className="admin-logout-btn" onClick={handleLogout}>
            Log Out
          </button>
        </div>
      </header>

      {['payouts', 'leads', 'compliance'].includes(view) && (
        <Dashboard
          session={session}
          onLogout={handleLogout}
          activeView={view}
          onPendingLeadsCountChange={setPendingLeadsCount}
        />
      )}
      {view === 'blanks' && <BlanksOrders session={session} onLogout={handleLogout} />}
      {view === 'disputes' && <Disputes session={session} onLogout={handleLogout} />}
      {view === 'broadcast' && <Broadcast session={session} onLogout={handleLogout} />}
    </div>
  );
}

export default App;
